class EdmeFullSlider {
  constructor(slider) {
    this.slider = slider;
    this.viewport = slider.querySelector(".edme-full-slider__viewport");
    this.track = slider.querySelector(".edme-full-slider__track");
    this.slides = Array.from(slider.querySelectorAll(".edme-full-slider__slide"));
    this.dots = Array.from(slider.querySelectorAll(".edme-full-slider__dot"));

    this.slideCount = this.slides.length;
    this.currentIndex = 0;

    this.timer = null;
    this.autoplay = slider.dataset.autoplay === "true";
    this.speed = Number(slider.dataset.speed) || 5000;

    this.isDragging = false;
    this.pointerId = null;
    this.startX = 0;
    this.currentX = 0;
    this.dragX = 0;
    this.hasDragged = false;
    this.dragDirection = null;
    this.suppressClick = false;

    this.dragStartThreshold = 14;

    if (!this.slider || !this.viewport || !this.track || !this.slideCount) return;

    this.init();
  }

  init() {
    this.setProgressDuration();
    this.bindEvents();
    this.goToSlide(0, false);

    if (this.autoplay && this.slideCount > 1) {
      this.start();
    }
  }

  getProgress(dot) {
    return (
      dot.querySelector(".edme-full-slider__dot-progress") ||
      dot.querySelector(".edme-full-slider__dot-fill")
    );
  }

  setProgressDuration() {
    this.dots.forEach((dot) => {
      const progress = this.getProgress(dot);

      if (progress) {
        progress.style.animationDuration = `${this.speed}ms`;
      }
    });
  }

  bindEvents() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.goToSlide(index);
        this.restart();
      });
    });

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    });

    this.slider.addEventListener("pointerdown", (event) => {
      this.onPointerDown(event);
    });

    window.addEventListener("pointermove", (event) => {
      this.onPointerMove(event);
    });

    window.addEventListener("pointerup", (event) => {
      this.onPointerUp(event);
    });

    window.addEventListener("pointercancel", (event) => {
      this.onPointerUp(event);
    });

    this.slider.addEventListener(
      "click",
      (event) => {
        if (!this.suppressClick) return;

        event.preventDefault();
        event.stopPropagation();

        this.suppressClick = false;
      },
      true
    );

    this.slider.addEventListener("dragstart", (event) => {
      event.preventDefault();
    });

    window.addEventListener("resize", () => {
      this.goToSlide(this.currentIndex, false);
    });
  }

  onPointerDown(event) {
    if (this.slideCount <= 1) return;
    if (event.target.closest(".edme-full-slider__dot")) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    this.isDragging = true;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.currentX = event.clientX;
    this.dragX = 0;
    this.hasDragged = false;
    this.dragDirection = null;
    this.suppressClick = false;
  }

  onPointerMove(event) {
    if (!this.isDragging) return;
    if (this.pointerId !== event.pointerId) return;

    this.currentX = event.clientX;
    this.dragX = this.currentX - this.startX;

    if (Math.abs(this.dragX) <= this.dragStartThreshold) return;

    if (!this.hasDragged) {
      this.hasDragged = true;
      this.suppressClick = true;
      this.slider.classList.add("is-dragging");
      this.pause();

      try {
        this.slider.setPointerCapture(event.pointerId);
      } catch (error) {}
    }

    event.preventDefault();

    if (!this.dragDirection) {
      if (this.dragX < 0) {
        this.dragDirection = "next";
      }

      if (this.dragX > 0) {
        this.dragDirection = "prev";
      }
    }

    if (!this.dragDirection) return;

    const isFirstSlide = this.currentIndex === 0;
    const isLastSlide = this.currentIndex === this.slideCount - 1;

    if (isFirstSlide && this.dragDirection === "prev") {
      this.setTrackPosition(this.currentIndex, 0);
      return;
    }

    if (isLastSlide && this.dragDirection === "next") {
      this.setTrackPosition(this.currentIndex, 0);
      return;
    }

    const sliderWidth = this.getSliderWidth();
    let visualDragX = this.dragX;

    if (this.dragDirection === "next") {
      visualDragX = Math.max(-sliderWidth, Math.min(0, visualDragX));
    }

    if (this.dragDirection === "prev") {
      visualDragX = Math.min(sliderWidth, Math.max(0, visualDragX));
    }

    this.setTrackPosition(this.currentIndex, visualDragX);
  }

  onPointerUp(event) {
    if (!this.isDragging) return;
    if (this.pointerId !== event.pointerId) return;

    const distance = this.currentX - this.startX;
    const threshold = Math.min(90, this.getSliderWidth() * 0.14);

    try {
      this.slider.releasePointerCapture(event.pointerId);
    } catch (error) {}

    this.isDragging = false;
    this.pointerId = null;
    this.slider.classList.remove("is-dragging");

    if (!this.hasDragged) {
      this.dragX = 0;
      this.dragDirection = null;
      return;
    }

    const isFirstSlide = this.currentIndex === 0;
    const isLastSlide = this.currentIndex === this.slideCount - 1;

    if (!this.dragDirection || Math.abs(distance) < threshold) {
      this.goToSlide(this.currentIndex);
      this.restart();

      setTimeout(() => {
        this.hasDragged = false;
        this.suppressClick = false;
      }, 250);

      return;
    }

    if (this.dragDirection === "next") {
      if (!isLastSlide) {
        this.goToSlide(this.currentIndex + 1);
      } else {
        this.goToSlide(this.currentIndex);
      }
    }

    if (this.dragDirection === "prev") {
      if (!isFirstSlide) {
        this.goToSlide(this.currentIndex - 1);
      } else {
        this.goToSlide(this.currentIndex);
      }
    }

    this.dragDirection = null;
    this.restart();

    setTimeout(() => {
      this.hasDragged = false;
      this.suppressClick = false;
    }, 250);
  }

  getSliderWidth() {
    return this.viewport.offsetWidth || this.slider.offsetWidth || window.innerWidth;
  }

  setTrackPosition(index, dragOffset = 0) {
    const sliderWidth = this.getSliderWidth();
    const offset = -index * sliderWidth + dragOffset;

    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
  }

  goToSlide(index, animate = true) {
    if (this.slideCount <= 1) {
      this.currentIndex = 0;
      this.setTrackPosition(0);
      this.updateActiveState(0);
      this.updateDots(0);
      return;
    }

    let targetIndex = index;

    if (targetIndex < 0) {
      targetIndex = 0;
    }

    if (targetIndex > this.slideCount - 1) {
      targetIndex = this.slideCount - 1;
    }

    this.currentIndex = targetIndex;

    if (!animate) {
      this.track.style.transition = "none";
      this.setTrackPosition(targetIndex);

      this.track.offsetHeight;

      this.track.style.transition = "";
    } else {
      this.track.style.transition = "";
      this.setTrackPosition(targetIndex);
    }

    this.updateActiveState(targetIndex);
    this.updateDots(targetIndex);
  }

  updateActiveState(index) {
    this.slides.forEach((slide, slideIndex) => {
      slide.classList.toggle("is-active", slideIndex === index);
    });
  }

  updateDots(index) {
    this.dots.forEach((dot, dotIndex) => {
      const progress = this.getProgress(dot);
      const isActive = dotIndex === index;

      dot.classList.toggle("is-active", isActive);

      if (!progress) return;

      progress.style.animation = "none";
      progress.style.transform = "scaleX(0)";
      progress.style.opacity = "0";

      progress.offsetHeight;

      if (isActive) {
        progress.style.opacity = "1";
        progress.style.animationName = "edmeSliderDotProgress";
        progress.style.animationTimingFunction = "linear";
        progress.style.animationFillMode = "forwards";
        progress.style.animationDuration = `${this.speed}ms`;
      }
    });
  }

  next() {
    if (this.currentIndex >= this.slideCount - 1) {
      this.goToSlide(0, false);
      return;
    }

    this.goToSlide(this.currentIndex + 1);
  }

  prev() {
    if (this.currentIndex <= 0) {
      this.goToSlide(0);
      return;
    }

    this.goToSlide(this.currentIndex - 1);
  }

  start() {
    this.clear();

    this.timer = setInterval(() => {
      this.next();
    }, this.speed);
  }

  clear() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    if (!this.autoplay || this.slideCount <= 1) return;

    this.clear();
    this.start();
  }

  pause() {
    if (!this.autoplay) return;

    this.slider.classList.add("is-paused");
    this.clear();
  }

  resume() {
    if (!this.autoplay) return;
    if (this.isDragging) return;

    this.slider.classList.remove("is-paused");
    this.restart();
  }
}

const initEdmeFullSliders = () => {
  const sliders = document.querySelectorAll("[data-edme-full-slider]");

  sliders.forEach((slider) => {
    if (slider.dataset.initialized === "true") return;

    slider.dataset.initialized = "true";
    new EdmeFullSlider(slider);
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initEdmeFullSliders);
} else {
  initEdmeFullSliders();
}

document.addEventListener("shopify:section:load", initEdmeFullSliders);